import type { ReactNode } from 'react'
// Иконки узлов — те же SVG, что у узлов карты в вебе (academy-frontend
// `assets/icons/gamified-lesson-types/`): книга у доступного, замок у закрытого.
import lockLesson from '@/assets/roadmap/lock-lesson.svg'
import theoryLesson from '@/assets/roadmap/theory-lesson.svg'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  buildRoadmapPath,
  buildRoadmapRibbon,
  getRoadmapMinHeight,
  NODE_BOX,
  NODE_SIZE,
  RIBBON_PADDING,
  ribbonMascotCenters,
  ROADMAP_WIDTH,
  TOP_ROOM,
} from '../../lib/roadmapPath'
import { cn } from '@/lib/utils'
import { useMessages } from '@/i18n'
import { messages } from './messages'

// Карта курса — порт того, что видит ученик в веб-академии (CourseProgressPath): серо-
// сиреневый фон с узорами, белая пунктирная змейка и узлы уроков, маскоты сбоку теми же
// отступами. Это иллюстрация с фиксированной палитрой плеера, а не тема студии, поэтому
// цвета заданы прямо здесь (токены приложения к ней не применимы).

/** Зазор между узлом и плашкой «Начать» (в плеере 3px + 5px покачивания). */
const START_LABEL_GAP = 8

interface Props {
  /** Сколько узлов рисовать — столько, сколько уроков реально создано. */
  lessonsCount: number
  /** Слоты маскотов по порядку: чётные справа от дорожки, нечётные слева (как в плеере). */
  slots: ReactNode[]
}

const LessonNodeView = ({ index }: { index: number }) => {
  const active = index === 0
  return (
    <div className="p-[23px]">
      <div
        className={cn(
          'flex size-[3.375rem] items-center justify-center rounded-full border border-white',
          active
            ? 'border-[#460466] bg-gradient-to-b from-[#ffcbb1] to-[#fed912] shadow-[inset_0_2px_0_0_rgba(255,255,255,0.5)]'
            : 'bg-gradient-to-b from-[#f7f7f8] to-white shadow-[0_1px_2px_0_rgba(70,4,102,0.04),0_6px_20px_0_rgba(70,4,102,0.06)]',
        )}
      >
        <span
          className={cn(
            'flex size-10 items-center justify-center rounded-full text-[#460466]',
            active
              ? 'bg-gradient-to-b from-white/30 to-transparent'
              : 'bg-gradient-to-b from-[#460466]/10 to-[#bf96ff]/10',
          )}
        >
          <img
            src={active ? theoryLesson : lockLesson}
            alt=""
            width={30}
            height={30}
            className="size-[30px]"
          />
        </span>
      </div>
    </div>
  )
}

const StartLabel = () => {
  const m = useMessages(messages)
  return (
    <span className="mx-auto block max-w-fit rounded-full bg-white px-1.5 py-1 text-sm leading-none tracking-[0.01em] text-[#460466]">
      {m.start}
    </span>
  )
}

/** Узлы уроков и пунктир. Вертикальная карта и лента отличаются только геометрией. */
const RoadmapTrack = ({
  d,
  positions,
  lessonsCount,
}: {
  d: string
  positions: Array<{ x: number; y: number }>
  lessonsCount: number
}) => (
  <>
    {lessonsCount > 1 ? (
      <path
        d={d}
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="10 8"
      />
    ) : null}
    {positions.map((pos, i) => (
      <g key={`${pos.x}-${pos.y}`}>
        <foreignObject
          x={pos.x - NODE_BOX / 2}
          y={pos.y - NODE_BOX / 2}
          width={NODE_BOX}
          height={NODE_BOX}
        >
          <LessonNodeView index={i} />
        </foreignObject>
        {i === 0 ? (
          <foreignObject
            x={pos.x - NODE_BOX / 2}
            y={pos.y + NODE_SIZE / 2 + START_LABEL_GAP}
            width={NODE_BOX}
            height={NODE_BOX}
          >
            <StartLabel />
          </foreignObject>
        ) : null}
      </g>
    ))}
  </>
)

const MAP_BACKGROUND = {
  backgroundImage: 'url("/roadmap-bg.svg"), linear-gradient(180deg, #d9c0ff 0%, #f7f7f8 84.27%)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: '438px 438px, cover',
}

/**
 * Лента для ПК, как у ученика в вебе (`RoadmapRibbon`): змейка вправо в свою длину и маскоты
 * в карманах сверху и снизу. Длинная лента листается вбок. Короткая стоит по центру.
 */
const RoadmapRibbonPreview = ({ lessonsCount, slots }: Props) => {
  const { d, positions: allPositions, height } = buildRoadmapRibbon(lessonsCount)
  const positions = allPositions.slice(0, lessonsCount)
  const centers = ribbonMascotCenters(slots.length)
  const width = Math.max(
    buildRoadmapRibbon(lessonsCount).width,
    ...centers.map((c) => c.x + NODE_BOX),
  )
  return (
    <div className="relative overflow-hidden rounded-[18px]" data-testid="roadmap-ribbon">
      <div aria-hidden="true" className="absolute inset-0" style={MAP_BACKGROUND} />
      <div className="relative overflow-x-auto" style={{ padding: `${RIBBON_PADDING}px 24px` }}>
        <div className="relative mx-auto" style={{ width, height }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={width}
            height={height}
            viewBox={`0 -${TOP_ROOM} ${width} ${height}`}
            fill="none"
            overflow="visible"
            className="pointer-events-none relative z-50"
          >
            <RoadmapTrack d={d} positions={positions} lessonsCount={lessonsCount} />
          </svg>
          {centers.map((center, index) => (
            <div
              key={index}
              className="absolute z-10 flex size-[8.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
              style={{ left: center.x, top: center.y + TOP_ROOM }}
            >
              {slots[index]}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export const RoadmapPreview = (props: Props) => {
  const isMobile = useIsMobile()
  return isMobile ? <RoadmapColumnPreview {...props} /> : <RoadmapRibbonPreview {...props} />
}

/** Вертикальная карта для телефона. */
const RoadmapColumnPreview = ({ lessonsCount, slots }: Props) => {
  const { d, positions: allPositions, viewBoxHeight } = buildRoadmapPath(lessonsCount)
  // buildRoadmapPath всегда отдаёт стартовую точку: у курса без уроков узлов не рисуем.
  const positions = allPositions.slice(0, lessonsCount)

  return (
    <div
      className="relative mx-auto flex w-full max-w-[34rem] justify-center overflow-hidden rounded-[18px] pt-5"
      style={{ minHeight: getRoadmapMinHeight(Math.max(slots.length, 1)) }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height={viewBoxHeight + TOP_ROOM}
        viewBox={`0 -${TOP_ROOM} ${ROADMAP_WIDTH} ${viewBoxHeight + TOP_ROOM}`}
        fill="none"
        // Плашка «Начать» под последним узлом выходит за высоту пути (у курса из
        // одного-двух уроков) — без visible её обрезало по краю svg.
        overflow="visible"
        // Слои как в плеере (svg поверх), но кликам он не мешает: иначе перекрывает слоты маскотов.
        className="pointer-events-none z-50 w-full max-w-[31.25rem]"
      >
        <RoadmapTrack d={d} positions={positions} lessonsCount={lessonsCount} />
      </svg>

      {/* Колонка маскотов: абсолютная в flex-контейнере встаёт по центру, как в плеере */}
      <div className="absolute z-10 flex flex-col">
        {slots.map((slot, index) => (
          <div
            // Порядок слотов фиксирован (два места), ключ по индексу здесь стабилен.
            key={index}
            className={cn(
              'mt-[140px] flex size-40 items-center justify-center first:mt-[190px]',
              index % 2 === 0 ? 'ml-[11.5625rem]' : 'ml-0',
            )}
          >
            {slot}
          </div>
        ))}
      </div>

      <div aria-hidden="true" className="absolute inset-0 rounded-[18px]" style={MAP_BACKGROUND} />
    </div>
  )
}
